/**
 * Exame toxicológico do motorista — integração com a API.
 *
 * `registrarColeta` e `lancarLaudo` são chamadas distintas de propósito: no
 * servidor têm permissões diferentes, porque registrar a coleta é rotina de
 * quem atende e lançar o laudo é ato de quem recebeu o documento oficial.
 */

import apiClient from './apiClient';
import type { ApiResponse } from '../types/common';
import type {
  ToxicologicalExam,
  DadosDaColeta,
  DadosDoLaudo,
  PreviaEsocial,
} from '../types/toxicologicalExam';

export * from '../types/toxicologicalExam';

const BASE = '/exames-toxicologicos';

/**
 * O `apiClient` já devolve o corpo `{ message, data }` que o servidor mandou, e tipa
 * `data` como possivelmente ausente. Em vez de espalhar `?.` por todo método — que
 * devolveria `undefined` silencioso para a tela —, falha aqui, onde dá para dizer o
 * que estava sendo buscado.
 */
function corpo<T>(recebido: ApiResponse<T>, oQue: string): T {
  const valor = recebido.data;
  if (valor === undefined || valor === null) {
    throw new Error(`Resposta vazia do servidor ao buscar ${oQue}`);
  }
  return valor;
}

export const toxicologicalExamService = {
  async listar(): Promise<ToxicologicalExam[]> {
    const resposta = await apiClient.get<ToxicologicalExam[]>(BASE);
    return corpo(resposta, 'os exames toxicológicos');
  },

  async listarPorAtendimento(appointmentId: number): Promise<ToxicologicalExam[]> {
    const resposta = await apiClient.get<ToxicologicalExam[]>(`${BASE}/atendimento/${appointmentId}`);
    return corpo(resposta, 'os exames do atendimento');
  },

  async historicoDoFuncionario(employeeId: number): Promise<ToxicologicalExam[]> {
    const resposta = await apiClient.get<ToxicologicalExam[]>(`${BASE}/funcionario/${employeeId}`);
    return corpo(resposta, 'o histórico do funcionário');
  },

  async buscarPorId(id: number): Promise<ToxicologicalExam> {
    const resposta = await apiClient.get<ToxicologicalExam>(`${BASE}/${id}`);
    return corpo(resposta, 'o exame toxicológico');
  },

  async registrarColeta(dados: DadosDaColeta): Promise<ToxicologicalExam> {
    const resposta = await apiClient.post<ToxicologicalExam>(BASE, dados);
    return corpo(resposta, 'a coleta registrada');
  },

  async lancarLaudo(id: number, dados: DadosDoLaudo): Promise<ToxicologicalExam> {
    const resposta = await apiClient.patch<ToxicologicalExam>(`${BASE}/${id}`, dados);
    return corpo(resposta, 'o laudo lançado');
  },

  async excluir(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },

  /**
   * O que seria enviado ao eSocial e o que ainda falta.
   *
   * A tela mostra isso ANTES de transmitir — o governo rejeita sem dizer qual
   * campo faltou, e descobrir pelo retorno dele custa dias.
   */
  async previaEsocial(id: number): Promise<PreviaEsocial> {
    const resposta = await apiClient.get<PreviaEsocial>(`${BASE}/${id}/previa-esocial`);
    return corpo(resposta, 'a prévia do eSocial');
  },
};

export default toxicologicalExamService;
